<script lang="ts" setup>
import { Swiper } from 'swiper';
import { Mousewheel, Navigation, FreeMode, Thumbs, Zoom } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper/types';
import type { OfferImage } from '~/types/Catalog';

defineProps<{ images: OfferImage[]; video?: string | null; alt: string }>();

const selectedSlideIndex = defineModel<number>('selectedSlideIndex', { default: 0 });
const isShow = defineModel<boolean>('isShow', { default: false });

watch(
  () => isShow.value,
  () => {
    if (isShow.value) {
      handleFullscreenOpen();
    } else {
      handleFullscreenClose();
    }
  },
);

const thumbsSwiperInstance = ref<SwiperType | null>(null);
const thumbsSwiperBlock = ref();

function thumbsSliderInitialization() {
  thumbsSwiperInstance.value = new Swiper(thumbsSwiperBlock.value, {
    freeMode: true,
    modules: [Mousewheel, FreeMode, Thumbs],
    mousewheel: true,
    slidesPerView: 'auto',
    watchSlidesProgress: true,
  });

  thumbsSwiperInstance.value.activeIndex = selectedSlideIndex.value;
}

const mainSwiperInstance = ref<SwiperType | null>(null);
const mainSwiperBlock = ref();

function mainSliderInitialization() {
  mainSwiperInstance.value = new Swiper(mainSwiperBlock.value, {
    modules: [Navigation, Thumbs, Zoom],
    navigation: {
      prevEl: '.swiper-button--prev',
      nextEl: '.swiper-button--next',
    },
    rewind: true,
    slidesPerView: 1,
    zoom: { toggle: true },
    spaceBetween: 12,
    thumbs: { swiper: thumbsSwiperInstance.value },
  });

  mainSwiperInstance.value.slideTo(selectedSlideIndex.value);
  mainSwiperInstance.value.on('slideChange', (swiper: SwiperType) => (selectedSlideIndex.value = swiper.activeIndex));
}

function handleKeyPress(event: KeyboardEvent) {
  if (event.key === 'Escape') {
    isShow.value = false;
  }
  if (event.key === 'ArrowRight') {
    mainSwiperInstance.value?.slideNext();
  }
  if (event.key === 'ArrowLeft') {
    mainSwiperInstance.value?.slidePrev();
  }
}

onMounted(() => {
  if (isShow.value) handleFullscreenOpen();
  document.addEventListener('keydown', handleKeyPress);
});

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeyPress);
  handleFullscreenClose();
});

const siteStore = useSiteStore();

async function handleFullscreenOpen() {
  siteStore.toggleBodyScrollLock(true);

  await nextTick();

  thumbsSliderInitialization();
  mainSliderInitialization();
  document.addEventListener('keydown', handleKeyPress);
}

function handleFullscreenClose() {
  mainSwiperInstance.value = null;
  thumbsSwiperInstance.value = null;
  siteStore.toggleBodyScrollLock(false);
}
</script>

<template>
  <Teleport to="body">
    <div
      v-if="isShow"
      class="fullscreen-image-gallery"
    >
      <div class="fullscreen-image-gallery__content">
        <div
          ref="mainSwiperBlock"
          class="swiper fullscreen-image-gallery__main-swiper"
        >
          <div class="swiper-wrapper">
            <div
              v-for="image in images"
              :key="image.url"
              class="swiper-slide fullscreen-image-gallery__image"
            >
              <div class="swiper-zoom-container">
                <picture class="flex place-content-center size-full">
                  <source
                    :srcset="image.conversions_webp.thumb_url_1480"
                    class="object-contain"
                    type="image/webp"
                  />
                  <img
                    :alt
                    :src="image.url"
                    class="object-contain"
                  />
                </picture>
              </div>
            </div>

            <div
              v-if="video"
              class="swiper-slide fullscreen-image-gallery__video bg-white"
            >
              <iframe
                :src="video"
                allow="autoplay; encrypted-media; fullscreen; picture-in-picture; screen-wake-lock"
                allowfullscreen
                height="100%"
                width="100%"
              ></iframe>
            </div>
          </div>

          <section
            v-if="images.length > 1"
            class="swiper-navigation"
          >
            <div class="swiper-button swiper-button--prev">
              <SvgoChevronLeft
                class="text-2xl"
                filled
              />
            </div>

            <div class="swiper-button swiper-button--next">
              <SvgoChevronRight
                class="text-2xl"
                filled
              />
            </div>
          </section>
        </div>

        <div
          ref="thumbsSwiperBlock"
          class="swiper fullscreen-image-gallery__thumbnails_swiper"
        >
          <div class="swiper-wrapper">
            <div
              v-for="(image, key) in images"
              :key="image.url"
              :class="{
                'fullscreen-image-gallery__thumbnail-image--selected': key === mainSwiperInstance?.activeIndex,
              }"
              class="swiper-slide fullscreen-image-gallery__thumbnail-image"
              @click="mainSwiperInstance?.slideTo(key)"
            >
              <picture class="flex size-full place-content-center">
                <source
                  :srcset="image.conversions_webp.thumb_url_180"
                  class="object-contain rounded-lg"
                  type="image/webp"
                />
                <source
                  :srcset="image.conversions.thumb_url_65"
                  class="object-contain rounded-lg"
                  type="image/jpeg"
                />
                <img
                  :alt
                  :src="image.url"
                  class="object-contain rounded-lg"
                />
              </picture>
            </div>

            <div
              v-if="video"
              :class="{ 'fullscreen-image-gallery__thumbnail-image--selected': selectedSlideIndex === images.length }"
              class="swiper-slide fullscreen-image-gallery__thumbnail-image fullscreen-image-gallery__thumbnail-image--video"
              @click="mainSwiperInstance?.slideTo(images.length)"
            >
              <SvgoVideoButton
                :font-controlled="false"
                class="size-10 absolute text-violet"
                filled
              />
              <picture class="flex place-content-center size-full rounded-lg overflow-hidden object-contain">
                <source
                  v-if="images[0].conversions_webp"
                  :srcset="`${images[0].conversions_webp.thumb_url_65}, ${images[0].conversions_webp.thumb_url_180} 2x`"
                  type="image/webp"
                />

                <source
                  v-if="images[0].conversions"
                  :srcset="images[0].conversions.thumb_url_65"
                  type="image/jpeg"
                />

                <img
                  :alt
                  :src="images[0].url"
                  class="object-contain"
                />
              </picture>
            </div>
          </div>
        </div>

        <button
          class="fullscreen-image-gallery__close-btn"
          @click="isShow = false"
        >
          <SvgoCross
            :font-controlled="false"
            filled
          />
        </button>
      </div>
    </div>
  </Teleport>
</template>

<style lang="scss" scoped>
.fullscreen-image-gallery {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 130;
  display: flex;
  width: 100vw;
  height: 100vh;
  background-color: $white;

  &__close-btn {
    position: absolute;
    top: 20px;
    right: 20px;
    z-index: 10;
    width: 36px;
    height: 36px;
    border-radius: 8px;
    background-color: $white;
    opacity: 0.6;
    cursor: pointer;
    transition: all 0.3s;

    &:hover {
      background-color: $black-5;
      opacity: 1;
    }

    @include screen-md {
      top: 24px;
      right: 24px;
      width: 48px;
      height: 48px;
      border-radius: 12px;
    }
  }

  &__content {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 100%;
    height: 100%;
    padding: 8px;
    background-color: $black-10;

    &:not(:hover) .swiper-navigation {
      opacity: 0;
    }

    @include screen-sm {
      padding: 16px;
    }
  }

  &__main-swiper {
    flex-grow: 1;
    overflow: hidden;
    width: 100%;
    border-radius: 12px;
  }

  &__thumbnails_swiper {
    flex-shrink: 0;
    width: 100%;
  }

  &__thumbnail-image {
    flex-shrink: 0;
    width: 80px;
    height: 80px;
    margin-right: 8px;
    padding: 4px;
    border: 1px solid transparent;
    border-radius: 12px;
    background-color: $white;
    cursor: pointer;
    transition: all 0.3s;

    &--video {
      display: flex;
      justify-content: center;
      align-items: center;
      object-fit: contain;
    }

    &:hover {
      border-color: $black-20;
    }

    &--selected {
      border-color: $black-80 !important;
    }
  }

  &__image {
    display: flex;
    justify-content: center;
    object-fit: contain;
    overflow: hidden;
    width: 100%;
    border-radius: 12px;
    background-color: $white;
    cursor: grab;
  }

  &__video {
    position: relative;
    container-type: size;

    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      z-index: 1;
      width: 100%;
      height: calc(100% - 12cqh);
      clip-path: polygon(
        0% 0%,
        0% 100%,
        41% 100%,
        41% 49%,
        59% 49%,
        59% 65%,
        0% 65%,
        0% 100%,
        100% 100%,
        100% 0%
      ); // overlay to show play btn

      @include screen-md {
        display: none;
      }
    }
  }
}
</style>
